/* Skup (sell.kickback.pl) — reveal / FAQ / katalog / modal oferty / PhotoDrop / kreator.
 * Kontrakt selektorów i REST: wp-plugins/kickback-skup/CONTRACT.md.
 * Port 1:1 z sell-app (React): SellWizard, OfferModal, Catalog, Faq, PhotoDrop, Modal, Reveal.
 * ponytail: stan żyje w DOM + instancjach PhotoDrop; fetch + JSON; zero deps, bez jQuery.
 */
import { createFocusTrap } from './focus-trap.js';

// ── słownik PL (sell-app/src/lib/i18n.ts) ──────────────────────────────────
const T = {
  errors: {
    required: 'Uzupełnij wszystkie wymagane pola.',
    condition: 'Wybierz stan produktu.',
    price: 'Podaj poprawną cenę (większą od 0).',
    photos: 'Dodaj od 2 do 6 zdjęć każdego produktu.',
    email: 'Podaj poprawny adres e-mail.',
    phone: 'Podaj poprawny numer telefonu.',
    zip: 'Kod pocztowy w formacie 00-000.',
    bank: 'Podaj poprawny numer konta (26 cyfr, opcjonalnie PL).',
    consent: 'Zaakceptuj regulamin skupu.',
    items: 'Dodaj od 1 do 4 produktów.',
    generic: 'Coś poszło nie tak. Spróbuj ponownie za chwilę.',
  },
  photos: {
    uploading: 'Wgrywanie…',
    remove: 'Usuń zdjęcie',
    errType: 'Dozwolone formaty: JPG, PNG, WEBP.',
    errSize: 'Plik jest większy niż 10 MB.',
    errCount: (max) => `Maksymalnie ${max} zdjęć.`,
    errUpload: 'Nie udało się wgrać zdjęcia. Spróbuj ponownie.',
  },
  sending: 'Wysyłanie…',
  chooseSize: 'Wybierz rozmiar',
  otherSize: 'Inny / EU',
  buying: 'Skupujemy',
  upTo: 'do',
  shown: (x, y) => `Pokazano ${x} z ${y} produktów`,
  count: (n) => plural(n, 'produkt', 'produkty', 'produktów'),
};

const CONDITIONS = ['Nowy z metką', 'Bardzo dobry', 'Dobry', 'Ślady użytkowania'];
const DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const PHOTO_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const MAX_ITEMS = 4;
const MIN_PHOTOS = 2;
const MAX_PHOTOS = 6;
const PAGE = 12;

// ── walidatory (sell-app/src/lib/validation.ts, status.ts) ─────────────────
export const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
export const isZip = (v) => /^\d{2}-\d{3}$/.test(String(v).trim());
export const isPhone = (v) => /^[+]?[\d\s-]{9,18}$/.test(String(v).trim());
export const isBankAccount = (v) => /^(PL)?\d{26}$/.test(String(v).replace(/\s/g, '').toUpperCase());
export const nonEmpty = (v) => String(v).trim().length > 0;
export const isPrice = (v) => { const n = Number(String(v).replace(',', '.')); return Number.isFinite(n) && n > 0; };

/** 1 produkt / 2–4 produkty / 5+ produktów (także 22, 23, 24…) */
export function plural(n, one, few, many) {
  const abs = Math.abs(n);
  if (abs === 1) return `${n} ${one}`;
  const last = abs % 10, tens = abs % 100;
  if (last >= 2 && last <= 4 && !(tens >= 12 && tens <= 14)) return `${n} ${few}`;
  return `${n} ${many}`;
}

/** „1 200 zł" (NBSP jako separator tysięcy, jak kbs_fmt_pln) */
export function fmtPln(n) {
  if (n == null || n === '') return ''; // Number('') === 0 — pusta cena to brak ceny
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  const [int, dec] = (Number.isInteger(v) ? String(v) : v.toFixed(2)).split('.');
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + (dec ? ',' + dec : '') + ' zł';
}

// ── REST ───────────────────────────────────────────────────────────────────
const cfg = () => window.KB_SKUP || { rest: '/wp-json/skup/v1/', nonce: '' };

/** POST do skup/v1. Błąd = Error z {code, field, server} (server = odpowiedź JSON wtyczki). */
async function post(path, body) {
  const init = body instanceof FormData
    ? { method: 'POST', body, credentials: 'same-origin' }
    : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), credentials: 'same-origin' };
  const r = await fetch(cfg().rest + path, init);
  const json = await r.json().catch(() => null);
  if (!r.ok || !json) {
    const e = new Error((json && json.message) || 'generic');
    e.code = (json && json.code) || null;
    e.field = (json && ((json.data && json.data.field) || json.field)) || null;
    e.server = !!json;
    throw e;
  }
  return json;
}
const errText = (e) => (e && e.code && T.errors[e.code]) || (e && e.server && e.message) || T.errors.generic;
const setInvalid = (el, on) => { if (el) { if (on) el.setAttribute('aria-invalid', 'true'); else el.removeAttribute('aria-invalid'); } };
const pad2 = (n) => String(n).padStart(2, '0');
const hpValue = (form) => { const el = form.elements.namedItem('kb_hp'); return el ? el.value : ''; };

// ── reveal ─────────────────────────────────────────────────────────────────
function initReveal() {
  const els = document.querySelectorAll('.reveal');
  if (!els.length) return;
  if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
  els.forEach((el) => io.observe(el));
}

// ── FAQ (jedno otwarte naraz) ──────────────────────────────────────────────
function setFaq(btn, open) {
  btn.setAttribute('aria-expanded', String(open));
  const panel = document.getElementById(btn.getAttribute('aria-controls') || '');
  if (panel) panel.classList.toggle('is-open', open);
  const icon = btn.querySelector('[data-faq-icon]');
  if (icon) icon.textContent = open ? '–' : '+';
}
function initFaq() {
  document.querySelectorAll('[data-faq]').forEach((root) => {
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-faq-btn]');
      if (!btn || !root.contains(btn)) return;
      const wasOpen = btn.getAttribute('aria-expanded') === 'true';
      root.querySelectorAll('[data-faq-btn]').forEach((b) => setFaq(b, false));
      if (!wasOpen) setFaq(btn, true);
    });
  });
}

// ── katalog (chipy / sort / pokaż więcej) ──────────────────────────────────
function initCatalog() {
  const root = document.querySelector('[data-catalog]');
  const grid = root && root.querySelector('[data-grid]');
  if (!grid) return;
  const cards = Array.from(grid.querySelectorAll('article[data-product]'));
  const chips = Array.from(root.querySelectorAll('.chip[data-cat]'));
  const countEl = root.querySelector('[data-count]');
  const shownEl = root.querySelector('[data-shown]');
  const progress = root.querySelector('[data-progress]');
  const fill = root.querySelector('[data-progress-fill]');
  const more = root.querySelector('[data-more]');
  const sortSel = root.querySelector('[data-sort]');

  const priceOf = (el) => (el.dataset.price === '' || el.dataset.price == null ? null : Number(el.dataset.price));
  const byPrice = (dir) => (a, b) => {
    const pa = priceOf(a), pb = priceOf(b);
    if (pa == null) return pb == null ? 0 : 1; // nulls last
    if (pb == null) return -1;
    return (pa - pb) * dir;
  };
  const SORTERS = { max: byPrice(-1), min: byPrice(1), new: (a, b) => Number(b.dataset.ts || 0) - Number(a.dataset.ts || 0) };

  let cat = '', sort = (sortSel && sortSel.value) || 'max', shown = PAGE;

  function render() {
    const list = cards.filter((c) => !cat || c.dataset.cat === cat).sort(SORTERS[sort] || SORTERS.max);
    list.forEach((c) => grid.appendChild(c)); // kolejność DOM = kolejność sortu
    const y = list.length, x = Math.min(shown, y);
    cards.forEach((c) => { c.hidden = true; });
    list.slice(0, x).forEach((c) => { c.hidden = false; });
    if (countEl) countEl.textContent = T.count(y);
    if (shownEl) shownEl.textContent = T.shown(x, y);
    if (fill) fill.style.width = (y ? (x / y) * 100 : 0) + '%';
    if (progress) { progress.setAttribute('aria-valuemax', String(y)); progress.setAttribute('aria-valuenow', String(x)); progress.setAttribute('aria-label', T.shown(x, y)); }
    if (more) more.hidden = x >= y;
    chips.forEach((ch) => { const on = (ch.dataset.cat || '') === cat; ch.classList.toggle('is-active', on); ch.setAttribute('aria-pressed', String(on)); });
  }

  chips.forEach((ch) => ch.addEventListener('click', () => { cat = ch.dataset.cat || ''; shown = PAGE; render(); }));
  if (sortSel) sortSel.addEventListener('change', () => { sort = sortSel.value; shown = PAGE; render(); });
  if (more) more.addEventListener('click', () => { shown += PAGE; render(); });
  render();
}

// ── PhotoDrop ──────────────────────────────────────────────────────────────
/** Fabryka nad [data-photodrop]. API: getPaths(), count(), reset(), setDisabled(b). opts.onChange(paths). */
function createPhotoDrop(root, opts = {}) {
  const zone = root.querySelector('[data-dropzone]');
  const input = root.querySelector('[data-drop-input]');
  const list = root.querySelector('[data-drop-list]');
  const errEl = root.querySelector('[data-drop-error]');
  const values = root.querySelector('[data-drop-values]');
  const label = root.querySelector('[data-drop-label]');
  const max = Number(root.dataset.max) || MAX_PHOTOS;
  const idleLabel = label ? label.textContent : '';
  let items = []; // {id, url}
  let busy = 0, disabled = false, gen = 0;

  const nameFor = () => {
    if (root.dataset.name) return root.dataset.name;
    const m = /^f-(\d+)-photos$/.exec(root.dataset.field || '');
    return m ? `items[${m[1]}][photos][]` : 'photos[]';
  };
  const setError = (msg) => { if (errEl) { errEl.textContent = msg || ''; errEl.hidden = !msg; } };

  function render() {
    if (zone) zone.hidden = items.length >= max;
    if (label) label.textContent = busy ? T.photos.uploading : idleLabel;
    if (list) {
      list.innerHTML = '';
      items.forEach((it, i) => {
        const li = document.createElement('li');
        li.className = 'relative overflow-hidden';
        li.style.cssText = 'width:72px;aspect-ratio:1/1;background:#ececea';
        const img = document.createElement('img');
        img.src = it.url; img.alt = ''; img.width = 72; img.height = 72;
        img.className = 'h-full w-full object-cover';
        const rm = document.createElement('button');
        rm.type = 'button'; rm.dataset.dropRemove = String(i); rm.setAttribute('aria-label', T.photos.remove); rm.textContent = '×';
        rm.className = 'absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-white text-[14px] leading-none text-black transition-colors duration-200 hover:bg-black hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-black active:scale-95';
        li.append(img, rm);
        list.appendChild(li);
      });
      list.hidden = !items.length;
    }
    if (values) {
      values.innerHTML = '';
      items.forEach((it) => { const h = document.createElement('input'); h.type = 'hidden'; h.name = nameFor(); h.value = it.id; values.appendChild(h); });
    }
    if (opts.onChange) opts.onChange(items.map((i) => i.id));
  }

  async function addFiles(fileList) {
    setError(null);
    let files = Array.from(fileList || []);
    if (!files.length || disabled) return;
    const room = max - items.length;
    if (files.length > room) { setError(T.photos.errCount(max)); files = files.slice(0, Math.max(0, room)); }
    const accepted = [];
    for (const f of files) {
      if (!PHOTO_MIME.includes(f.type)) { setError(T.photos.errType); continue; }
      if (f.size > MAX_PHOTO_BYTES) { setError(T.photos.errSize); continue; }
      accepted.push(f);
    }
    if (!accepted.length) return;
    const g = gen;
    busy += accepted.length; render();
    for (const f of accepted) {
      try {
        const fd = new FormData();
        fd.append('file', f); fd.append('_kbs', cfg().nonce); fd.append('kb_hp', '');
        const res = await post('upload', fd);
        if (!res || typeof res.id !== 'string') throw new Error('generic');
        if (g === gen && items.length < max) items.push({ id: res.id, url: URL.createObjectURL(f) });
      } catch (e) {
        if (g === gen) setError((e && e.server && e.message) || T.photos.errUpload);
      } finally {
        busy = Math.max(0, busy - 1);
      }
      if (g === gen) render();
    }
  }

  function reset() {
    gen++; // w locie wgrywane pliki nie trafią do nowego stanu
    items.forEach((i) => URL.revokeObjectURL(i.url));
    items = []; busy = 0;
    setError(null);
    if (zone) zone.classList.remove('is-over');
    render();
  }

  if (zone) {
    zone.addEventListener('click', () => { if (!disabled && input) input.click(); });
    zone.addEventListener('keydown', (e) => { if ((e.key === 'Enter' || e.key === ' ') && !disabled) { e.preventDefault(); if (input) input.click(); } });
    zone.addEventListener('dragover', (e) => { e.preventDefault(); zone.classList.add('is-over'); });
    zone.addEventListener('dragleave', () => zone.classList.remove('is-over'));
    zone.addEventListener('drop', (e) => { e.preventDefault(); zone.classList.remove('is-over'); if (!disabled) addFiles(e.dataTransfer && e.dataTransfer.files); });
  }
  if (input) input.addEventListener('change', () => { addFiles(input.files); input.value = ''; });
  if (list) list.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-drop-remove]');
    if (!btn) return;
    const i = Number(btn.dataset.dropRemove);
    if (items[i]) URL.revokeObjectURL(items[i].url);
    items = items.filter((_, j) => j !== i);
    render();
  });

  return {
    getPaths: () => items.map((i) => i.id),
    count: () => items.length,
    reset,
    setDisabled: (b) => { disabled = !!b; if (zone) zone.setAttribute('aria-disabled', String(disabled)); },
  };
}

// ── modal oferty ───────────────────────────────────────────────────────────
function initOffer() {
  const box = document.getElementById('kbs-offer');
  if (!box) return;
  const overlay = box.querySelector('[data-offer-overlay]') || box;
  const dialog = box.querySelector('[role="dialog"]') || box;
  const form = box.querySelector('[data-offer-form]');
  const title = document.getElementById('kbs-offer-title');
  const maxEl = box.querySelector('[data-offer-max]');
  const stForm = box.querySelector('[data-offer-state="form"]');
  const stSent = box.querySelector('[data-offer-state="sent"]');
  const refEl = box.querySelector('[data-offer-ref]');
  const errEl = box.querySelector('[data-offer-error]');
  const submit = box.querySelector('[data-offer-submit]');
  const sizeSel = document.getElementById('o-size');
  const condSel = document.getElementById('o-condition');
  const priceIn = document.getElementById('o-price');
  const emailIn = document.getElementById('o-email');
  const dropRoot = box.querySelector('[data-photodrop]');
  const drop = dropRoot ? createPhotoDrop(dropRoot) : null;
  const trap = createFocusTrap(dialog);
  const submitLabel = submit ? submit.textContent : '';
  let open = false, sending = false, product = null;

  const showErr = (msg) => { if (errEl) { errEl.textContent = msg || ''; errEl.hidden = !msg; } };
  const setState = (sent) => { if (stForm) stForm.hidden = sent; if (stSent) stSent.hidden = !sent; };
  const setSending = (b) => {
    sending = b;
    if (submit) { submit.disabled = b; submit.textContent = b ? T.sending : submitLabel; }
    if (drop) drop.setDisabled(b);
  };

  function fillSizes(sizes) {
    if (!sizeSel) return;
    sizeSel.innerHTML = '';
    const add = (value, text) => { const o = document.createElement('option'); o.value = value; o.textContent = text; sizeSel.appendChild(o); };
    add('', T.chooseSize);
    (sizes.length ? sizes : DEFAULT_SIZES).forEach((s) => add(s, s));
    add(T.otherSize, T.otherSize);
  }

  function openFor(article, opener) {
    // Safari nie fokusuje przycisku po kliknięciu → trap zapamiętałby <body>; fokus jawnie, żeby wrócił na opener.
    if (opener && typeof opener.focus === 'function') opener.focus();
    let sizes = [];
    try { sizes = JSON.parse(article.dataset.sizes || '[]'); } catch (_) { sizes = []; }
    if (!Array.isArray(sizes)) sizes = [];
    product = { id: article.dataset.id, name: article.dataset.name || '', max: article.dataset.max, sizes: sizes.map(String) };
    if (title) title.textContent = product.name;
    if (maxEl) {
      const has = product.max != null && product.max !== '' && Number.isFinite(Number(product.max));
      maxEl.textContent = has ? `${T.buying} ${T.upTo} ${fmtPln(product.max)}` : '';
      maxEl.hidden = !has;
    }
    fillSizes(product.sizes);
    if (form) form.reset();
    if (drop) drop.reset();
    showErr(null); setState(false); setSending(false);
    setInvalid(sizeSel, false); setInvalid(condSel, false);
    box.hidden = false;
    document.body.classList.add('no-scroll');
    open = true;
    trap.activate();
    // jak Modal.tsx: fokus na pierwszy input, nie na „×"
    setTimeout(() => {
      const f = dialog.querySelector('select:not([disabled]),input:not([hidden]):not([type="hidden"]):not([disabled]),textarea');
      if (f && open && !dialog.contains(document.activeElement)) f.focus(); // nie kradnij fokusu, gdy użytkownik już pisze
    }, 30);
  }

  function close() {
    if (!open) return;
    open = false;
    box.hidden = true;
    document.body.classList.remove('no-scroll');
    trap.deactivate(); // przywraca fokus na przycisk, który otworzył modal
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-offer-open]');
    if (!btn) return;
    const article = btn.closest('article[data-product]');
    if (!article) return;
    e.preventDefault();
    openFor(article, btn);
  });
  box.addEventListener('click', (e) => { if (e.target.closest('[data-offer-close]')) { e.preventDefault(); close(); } });
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
  dialog.addEventListener('kb:drawer-close', close); // Esc z focus-trap
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && open) close(); });

  if (form) form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (sending || !product) return;
    showErr(null);
    const size = sizeSel ? sizeSel.value : '';
    const condition = condSel ? condSel.value : '';
    const price = priceIn ? priceIn.value : '';
    const email = emailIn ? emailIn.value : '';
    const photos = drop ? drop.getPaths() : [];
    let msg = null;
    if (!size) msg = T.errors.required;
    else if (!CONDITIONS.includes(condition)) msg = T.errors.condition;
    else if (!isPrice(price)) msg = T.errors.price;
    else if (!isEmail(email)) msg = T.errors.email;
    else if (photos.length > MAX_PHOTOS) msg = T.errors.photos;
    setInvalid(sizeSel, !!msg && !size);
    setInvalid(condSel, !!msg && !condition);
    if (msg) return showErr(msg);
    setSending(true);
    try {
      const idNum = Number(product.id);
      const res = await post('offer', {
        product_id: Number.isFinite(idNum) && product.id !== '' ? idNum : product.id,
        size, condition, price: price.trim(), email: email.trim(), photos,
        kb_hp: hpValue(form), _kbs: cfg().nonce,
      });
      if (refEl) refEl.textContent = res.ref || '—';
      setState(true);
      requestAnimationFrame(() => { const c = (stSent || box).querySelector('[data-offer-close]'); if (c) c.focus(); });
    } catch (err) {
      showErr(errText(err));
      if (err && err.field) setInvalid(document.getElementById(err.field), true);
    } finally {
      setSending(false);
    }
  });
}

// ── kreator (SellWizard) ───────────────────────────────────────────────────
function initWizard() {
  const form = document.getElementById('kbs-wizard');
  if (!form) return;
  const tpl = document.getElementById('kbs-item-tpl');
  const itemsEl = form.querySelector('[data-items]');
  if (!itemsEl) return;
  const steps = { 1: form.querySelector('[data-step="1"]'), 2: form.querySelector('[data-step="2"]'), 3: form.querySelector('[data-step="3"]') };
  const addBtn = form.querySelector('[data-add-item]');
  const addCount = form.querySelector('[data-add-count]');
  const stepLabel = form.querySelector('[data-step-label]');
  const seg2 = form.querySelector('[data-seg="2"]');
  const formErr = form.querySelector('[data-form-error]');
  const submitBtn = form.querySelector('[data-submit]');
  const backBtn = form.querySelector('[data-back]');
  const nextBtn = form.querySelector('[data-next]');
  const submitLabel = submitBtn ? submitBtn.textContent : '';
  const drops = new Map(); // root → instancja PhotoDrop
  let step = 1, pending = false;

  const cards = () => Array.from(itemsEl.querySelectorAll('[data-item]'));
  const val = (id) => { const el = document.getElementById(id); return el ? el.value : ''; };

  // błędy: p.err[data-err-for=id] + aria-invalid na polu (dla zdjęć: root [data-photodrop][data-field=id])
  const fieldEl = (id) => document.getElementById(id) || form.querySelector(`[data-field="${id}"]`);
  const errSlot = (id) => form.querySelector(`[data-err-for="${id}"]`) || (() => { const f = fieldEl(id); return f && f.matches('[data-photodrop]') ? f.querySelector('[data-drop-error]') : null; })();
  function setError(id, msg) {
    const p = errSlot(id);
    if (p) { p.textContent = msg; p.hidden = false; }
    setInvalid(fieldEl(id), true);
  }
  function clearError(id) {
    const p = errSlot(id);
    if (p && !p.hidden) { p.textContent = ''; p.hidden = true; }
    setInvalid(fieldEl(id), false);
  }
  function clearErrors() {
    form.querySelectorAll('[data-err-for]').forEach((p) => { p.textContent = ''; p.hidden = true; });
    form.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.removeAttribute('aria-invalid'));
  }
  function focusField(id) {
    const el = fieldEl(id);
    if (!el) return;
    let target = el;
    if (!el.matches('input,select,textarea,button,a[href],[tabindex]')) {
      target = el.querySelector('[data-dropzone]:not([hidden]),button:not([hidden]),input:not([type="hidden"])') || el;
      if (target === el) el.tabIndex = -1;
    }
    target.focus();
  }
  /** Ustawia błędy, fokusuje pierwszy. Zwraca true gdy były błędy. */
  function applyErrors(errs) {
    clearErrors();
    const ids = Object.keys(errs);
    ids.forEach((id) => setError(id, errs[id]));
    if (ids.length) requestAnimationFrame(() => focusField(ids[0]));
    return ids.length > 0;
  }
  const setFormError = (msg) => { if (formErr) { formErr.textContent = msg || ''; formErr.hidden = !msg; } };

  function dropLimits(root) {
    return { min: root && root.dataset.min !== undefined ? Number(root.dataset.min) : MIN_PHOTOS, max: (root && Number(root.dataset.max)) || MAX_PHOTOS };
  }
  function mountDrops() {
    itemsEl.querySelectorAll('[data-photodrop]').forEach((root) => {
      if (drops.has(root)) return;
      drops.set(root, createPhotoDrop(root, {
        onChange: (paths) => { // jak SellWizard: błąd zdjęć znika, gdy jest ich dość
          const { min, max } = dropLimits(root);
          if (root.dataset.field && paths.length >= min && paths.length <= max) clearError(root.dataset.field);
        },
      }));
    });
  }

  function renumber() {
    const list = cards();
    const ATTRS = ['id', 'name', 'for', 'data-field', 'data-err-for', 'aria-controls', 'aria-describedby', 'aria-labelledby'];
    list.forEach((card, j) => {
      card.dataset.item = String(j);
      card.querySelectorAll(ATTRS.map((a) => `[${a}]`).join(',')).forEach((el) => {
        ATTRS.forEach((a) => {
          const v = el.getAttribute(a);
          if (v == null) return;
          const nv = v.replace(/^f-\d+-/, `f-${j}-`).replace(/^items\[\d+\]/, `items[${j}]`);
          if (nv !== v) el.setAttribute(a, nv);
        });
      });
      const t = card.querySelector('[data-item-title]');
      if (t) t.textContent = t.textContent.replace(/\d+/, pad2(j + 1));
      if (card.hasAttribute('aria-label')) card.setAttribute('aria-label', card.getAttribute('aria-label').replace(/\d+/, pad2(j + 1)));
      const rm = card.querySelector('[data-item-remove]');
      if (rm) rm.hidden = list.length <= 1;
    });
    if (addBtn) addBtn.hidden = list.length >= MAX_ITEMS;
    if (addCount) addCount.textContent = `${list.length}/${MAX_ITEMS}`;
  }

  function addItem() {
    if (!tpl) return;
    const i = cards().length;
    if (i >= MAX_ITEMS) return;
    itemsEl.insertAdjacentHTML('beforeend', tpl.innerHTML.replace(/\{i\}/g, String(i)).replace(/\{n\}/g, pad2(i + 1)));
    mountDrops();
    renumber();
  }

  function removeItem(card) {
    if (cards().length <= 1) return;
    card.querySelectorAll('[data-photodrop]').forEach((root) => { const d = drops.get(root); if (d) d.reset(); drops.delete(root); });
    card.remove();
    clearErrors();
    renumber();
  }

  function validateStep1() {
    const e = {};
    cards().forEach((card, i) => {
      if (!nonEmpty(val(`f-${i}-name`))) e[`f-${i}-name`] = T.errors.required;
      if (!nonEmpty(val(`f-${i}-size`))) e[`f-${i}-size`] = T.errors.required;
      if (!CONDITIONS.includes(val(`f-${i}-condition`))) e[`f-${i}-condition`] = T.errors.condition;
      if (!isPrice(val(`f-${i}-price`))) e[`f-${i}-price`] = T.errors.price;
      const root = card.querySelector('[data-photodrop]');
      const d = root && drops.get(root);
      const n = d ? d.count() : 0;
      const { min, max } = dropLimits(root);
      if (n < min || n > max) e[root && root.dataset.field ? root.dataset.field : `f-${i}-photos`] = T.errors.photos;
    });
    return e;
  }

  function validateStep2() {
    const e = {};
    const consent = document.getElementById('c-consent');
    if (!nonEmpty(val('c-fullName'))) e['c-fullName'] = T.errors.required;
    if (!isPhone(val('c-phone'))) e['c-phone'] = T.errors.phone;
    if (!isEmail(val('c-email'))) e['c-email'] = T.errors.email;
    if (!nonEmpty(val('c-street'))) e['c-street'] = T.errors.required;
    if (!isZip(val('c-zip'))) e['c-zip'] = T.errors.zip;
    if (!nonEmpty(val('c-city'))) e['c-city'] = T.errors.required;
    if (!isBankAccount(val('c-iban'))) e['c-iban'] = T.errors.bank;
    if (!(consent && consent.checked)) e['c-consent'] = T.errors.consent;
    return e;
  }

  function show(n) {
    step = n;
    [1, 2, 3].forEach((k) => {
      const el = steps[k];
      if (!el) return;
      const on = k === n;
      el.hidden = !on;
      if (on) { el.classList.remove('step-in'); void el.offsetWidth; el.classList.add('step-in'); } // restart animacji
    });
    if (stepLabel) stepLabel.textContent = pad2(Math.min(n, 2));
    if (seg2) seg2.classList.toggle('is-done', n >= 2);
    // ekran potwierdzenia (design) bez nagłówka kreatora i paska kroków
    document.querySelectorAll('[data-wizard-head],[data-wizard-bar]').forEach((el) => { el.hidden = n === 3; }); // hooki leżą poza <form>
    window.scrollTo(0, 0);
  }

  function splitLetters(el) {
    if (!el || el.dataset.split) return;
    el.dataset.split = '1';
    const text = el.textContent.trim();
    el.textContent = '';
    const sr = document.createElement('span');
    sr.className = 'sr-only'; sr.textContent = text;
    el.appendChild(sr);
    Array.from(text).forEach((ch, i) => {
      const s = document.createElement('span');
      s.className = 'letter-in'; s.style.animationDelay = `${i * 40}ms`; s.setAttribute('aria-hidden', 'true'); s.textContent = ch;
      el.appendChild(s);
    });
  }

  function next() {
    if (applyErrors(validateStep1())) return;
    show(2);
  }
  function back() {
    if (pending) return;
    clearErrors(); setFormError(null);
    show(1);
  }
  const setPending = (b) => {
    pending = b;
    if (submitBtn) { submitBtn.disabled = b; submitBtn.textContent = b ? T.sending : submitLabel; }
    if (backBtn) backBtn.disabled = b;
  };

  async function submit() {
    setFormError(null);
    if (applyErrors(validateStep2())) return;
    if (pending) return;
    setPending(true);
    const body = {
      items: cards().map((card, i) => {
        const root = card.querySelector('[data-photodrop]');
        const d = root && drops.get(root);
        return { name: val(`f-${i}-name`).trim(), size: val(`f-${i}-size`).trim(), condition: val(`f-${i}-condition`), price: val(`f-${i}-price`).trim(), photos: d ? d.getPaths() : [] };
      }),
      full_name: val('c-fullName').trim(), phone: val('c-phone').trim(), email: val('c-email').trim(),
      street: val('c-street').trim(), zip: val('c-zip').trim(), city: val('c-city').trim(), iban: val('c-iban').trim(),
      consent: true, kb_hp: hpValue(form), _kbs: cfg().nonce,
    };
    try {
      const res = await post('submission', body);
      const refEl = form.querySelector('[data-done-ref]');
      if (refEl) refEl.textContent = res.ref || '';
      splitLetters(form.querySelector('[data-gooool]'));
      show(3);
    } catch (err) {
      const msg = errText(err);
      setFormError(msg);
      if (err && err.field && fieldEl(err.field)) {
        if (/^f-\d+-/.test(err.field) && step !== 1) show(1);
        setError(err.field, msg);
        requestAnimationFrame(() => focusField(err.field));
      }
    } finally {
      setPending(false);
    }
  }

  // Przyciski: type=button → click; type=submit / Enter → zdarzenie submit (bez podwójnego odpalenia).
  if (nextBtn) nextBtn.addEventListener('click', () => { if (nextBtn.type !== 'submit') next(); });
  if (backBtn) backBtn.addEventListener('click', () => { if (backBtn.type !== 'submit') back(); });
  if (submitBtn) submitBtn.addEventListener('click', () => { if (submitBtn.type !== 'submit') submit(); });
  form.addEventListener('submit', (e) => { e.preventDefault(); if (step === 1) next(); else if (step === 2) submit(); });
  if (addBtn) addBtn.addEventListener('click', addItem);
  itemsEl.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-item-remove]');
    if (rm) { e.preventDefault(); const card = rm.closest('[data-item]'); if (card) removeItem(card); }
  });

  if (!cards().length) addItem(); else { mountDrops(); renumber(); }
  show(1);
}

// ── boot ───────────────────────────────────────────────────────────────────
function boot() {
  if (!document.body || !document.body.classList.contains('kb-skup')) return;
  const safe = window.__kbSafeInit || ((name, fn) => { try { fn(); } catch (e) { try { console.warn('[kb] init failed:', name, e && e.message); } catch (_) {} } });
  safe('skup:reveal', initReveal);
  safe('skup:faq', initFaq);
  safe('skup:catalog', initCatalog);
  safe('skup:offer', initOffer);
  safe('skup:wizard', initWizard);
}

if (typeof document !== 'undefined') {
  // Importy są hoistowane przed ciało main.js — mikrotask czeka, aż main.js ustawi window.__kbSafeInit.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else Promise.resolve().then(boot);
}
